import { FC, useEffect, useState } from 'react';
import classNames from 'classnames';

import { AppModalProps } from '@/types/modal.type';
import { ModalView } from '@/Components/ModalView';
import { Button } from '@/Components/Button';
import { suggestionImgMap } from '@/Components/FlowSuggestionRowItem/FlowSuggestionRowItem.constant';

const IMPORT_TEMPLATE_MODAL_CANCEL_ID = 'import-template-modal-cancel';

export type ImportTemplateModalProps = AppModalProps<{
    workspaceName: string;
    pendingTemplates: {
        slug: string;
        name: string;
        description: string;
    }[];
    onConfirm: () => Promise<void>;
    onCancel: () => void;
}>;

export const ImportTemplateModal: FC<ImportTemplateModalProps> = ({
    onClose,
    payload,
}) => {
    const {
        workspaceName,
        pendingTemplates,
        onCancel,
        onConfirm: confirmImport,
    } = payload;

    const scrollPendingList = pendingTemplates.length > 3;

    const [isImporting, setIsImporting] = useState(false);

    const onConfirmClick = () => {
        if (isImporting) {
            return;
        }

        setIsImporting(true);

        void confirmImport().finally(() => {
            setIsImporting(false);
            onClose();
        });
    };

    const onCancelClick = () => {
        if (isImporting) {
            return;
        }

        onClose();
    };

    useEffect(() => {
        const focusCancelTimer = window.setTimeout(() => {
            document
                .getElementById(IMPORT_TEMPLATE_MODAL_CANCEL_ID)
                ?.focus({ preventScroll: true });
        }, 0);

        return () => {
            window.clearTimeout(focusCancelTimer);
            onCancel();
        };
    }, []);

    return (
        <ModalView
            maxWidth="sm"
            closeable={!isImporting}
            onClose={onClose}
            onSubmit={isImporting ? undefined : onConfirmClick}
            contentContainerClassName="py-6 px-8 text-brand-ocean flex min-h-0 flex-col justify-center items-center !overflow-visible">
            <div className="w-full !font-normal text-body-text-15 text-left text-brand-ocean mb-2">
                Are you sure you want to import the following{' '}
                <span className="font-bold">
                    {pendingTemplates.length > 1
                        ? `${pendingTemplates.length} `
                        : ''}
                    template
                    {pendingTemplates.length > 1 ? 's' : ''}
                </span>{' '}
                into <span className="font-bold">{workspaceName}</span>{' '}
                workspace?
            </div>
            <div className="relative my-3 w-full shrink-0 rounded-md border border-gray-20/70">
                <div
                    tabIndex={scrollPendingList ? -1 : undefined}
                    className={classNames(
                        'flex w-full flex-col items-start justify-start rounded-md pb-4',
                        {
                            'min-h-0 max-h-72 overflow-y-auto overscroll-y-contain outline-none focus:outline-none [-webkit-mask-image:linear-gradient(to_bottom,black,black_calc(100%-1.75rem),transparent)] [mask-image:linear-gradient(to_bottom,black,black_calc(100%-1.75rem),transparent)]':
                                scrollPendingList,
                        },
                    )}>
                    {pendingTemplates.map((template, index) => (
                        <div
                            key={template.slug}
                            className={classNames(
                                'flex w-full items-center justify-center px-3 py-3',
                                {
                                    'border-b border-gray-20/70':
                                        index < pendingTemplates.length - 1,
                                },
                            )}>
                            <div className="aspect-square h-10 min-h-10 w-10 min-w-10 overflow-hidden rounded-md bg-gray-20">
                                <img
                                    src={
                                        // @ts-expect-error
                                        suggestionImgMap[template.slug] ||
                                        suggestionImgMap[
                                            'example_suggestion_homepage'
                                        ]
                                    }
                                    alt={template.name}
                                    className="h-full w-full object-cover"
                                />
                            </div>
                            <div className="ml-2">
                                <div className="text-small-text-13 text-brand-ocean">
                                    {template.name}
                                </div>
                                <div className="text-small-text-13 text-gray-80">
                                    {template.description}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="mt-2 w-full flex items-center justify-center space-x-3">
                <Button
                    id={IMPORT_TEMPLATE_MODAL_CANCEL_ID}
                    className="w-full h-9"
                    variant="default"
                    disabled={isImporting}
                    onClick={onCancelClick}>
                    Cancel
                </Button>
                <Button
                    className="w-full h-9"
                    variant="primary"
                    disabled={isImporting}
                    onClick={onConfirmClick}>
                    {isImporting ? 'Importing...' : 'Continue & Import'}
                </Button>
            </div>
        </ModalView>
    );
};
